import React, { useState, useEffect } from 'react';
import { 
  Crown, Zap, Star, Building2, Check, X, ArrowRight, ExternalLink, 
  Globe, UserPlus, ChevronDown
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const planIcons = { estandar: Zap, profesional: Star, empresarial: Building2, premium: Crown };
const planGradients = {
  estandar: 'bg-brand-green',
  profesional: 'bg-brand-deep',
  empresarial: 'bg-brand-copper',
  premium: 'bg-brand-copper'
};

const SubscriptionPlans = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [regions, setRegions] = useState({});
  const [selectedRegion, setSelectedRegion] = useState(() => localStorage.getItem('deal-region') || 'us');

  useEffect(() => {
    axios.get(`${API}/regions`).then(res => setRegions(res.data)).catch(() => {});
    axios.get(`${API}/subscriptions/current`).then(res => setCurrentSub(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/subscriptions/plans?region=${selectedRegion}`).then(res => setPlans(res.data)).catch(() => {});
    localStorage.setItem('deal-region', selectedRegion);
  }, [selectedRegion]);

  const handleSelectPlan = (planId) => {
    toast.info(`Plan "${planId}" seleccionado. La pasarela de pago se activará próximamente.`);
  };

  const fmtPrice = (amount, plan) => {
    if (!plan) return `$${amount}`;
    const sym = plan.symbol || '$';
    const cur = plan.currency || 'USD';
    if (amount >= 1000) {
      return `${sym}${Math.round(amount).toLocaleString()} ${cur}`;
    }
    return `${sym}${amount.toFixed(2)} ${cur}`;
  };

  const activeRegions = Object.entries(regions).filter(([, r]) => r.active);
  const inactiveRegions = Object.entries(regions).filter(([, r]) => !r.active);

  return (
    <div data-testid="subscription-page" className="min-h-screen">
      <Header title="Planes y Suscripción" subtitle="Elige el plan perfecto para tu equipo">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-muted-foreground" />
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger data-testid="region-selector" className="w-52 h-9">
              <SelectValue placeholder="Selecciona región" />
            </SelectTrigger>
            <SelectContent>
              {activeRegions.map(([code, r]) => (
                <SelectItem key={code} value={code}>
                  {r.name} ({r.currency})
                </SelectItem>
              ))}
              {inactiveRegions.length > 0 && (
                <>
                  <div className="px-2 py-1 text-xs text-muted-foreground border-t mt-1 pt-2">Próximamente</div>
                  {inactiveRegions.map(([code, r]) => (
                    <SelectItem key={code} value={code} disabled>
                      {r.name} ({r.currency})
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      </Header>

      <div className="p-6 max-w-6xl">
        {/* Current Plan */}
        {currentSub && (
          <Card className="glass-card mb-8 border-violet-500/20">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-lg capitalize">{currentSub.plan?.name || 'Trial'}</p>
                  <div className="flex items-center gap-2">
                    <Badge className={cn("text-xs",
                      currentSub.status === 'trial' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30')}>
                      {currentSub.status === 'trial' ? 'Prueba Gratuita 14 días' : 'Activo'}
                    </Badge>
                    {currentSub.trial_ends_at && (
                      <span className="text-xs text-muted-foreground">
                        Expira: {new Date(currentSub.trial_ends_at).toLocaleDateString('es')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, i) => {
            const PlanIcon = planIcons[plan.id] || Zap;
            const gradient = planGradients[plan.id] || 'bg-brand-green';
            const isCurrent = currentSub?.plan_id === plan.id;

            return (
              <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <Card className={cn(
                  "glass-card relative overflow-hidden h-full flex flex-col",
                  plan.popular && "ring-2 ring-violet-500/50 shadow-lg shadow-violet-500/10",
                  isCurrent && "border-emerald-500/50"
                )}>
                  {plan.popular && (
                    <div className="absolute top-0 right-0 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-bl-lg">
                      Popular
                    </div>
                  )}
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="mb-4">
                      <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-3", gradient)}>
                        <PlanIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-bold">{plan.name}</h3>
                      <div className="mt-2">
                        <span className="text-2xl font-bold">{fmtPrice(plan.price, plan)}</span>
                        <span className="text-sm text-muted-foreground">/mes</span>
                      </div>
                      {plan.region !== 'us' && (
                        <p className="text-xs text-muted-foreground mt-0.5">~${plan.price_usd} USD</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {plan.max_admins} Admin{plan.max_admins > 1 ? 's' : ''}
                        {plan.max_managers > 0 ? ` + ${plan.max_managers} Manager${plan.max_managers > 1 ? 's' : ''}` : ''}
                        {' + '}{plan.max_monitors} Monitor{plan.max_monitors > 1 ? 'es' : ''}
                      </p>
                    </div>

                    <div className="flex-1 space-y-2 mb-4">
                      {plan.features?.map((f, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-emerald-700 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-muted-foreground">{f}</span>
                        </div>
                      ))}
                      {plan.excluded?.map((f, j) => (
                        <div key={`ex-${j}`} className="flex items-start gap-2 opacity-50">
                          <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-muted-foreground line-through">{f}</span>
                        </div>
                      ))}
                      {plan.integrations?.map((intg, j) => (
                        <div key={`int-${j}`} className="flex items-start gap-2">
                          <ExternalLink className="w-4 h-4 text-fuchsia-700 dark:text-fuchsia-300 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-xs font-medium text-fuchsia-700 dark:text-fuchsia-300">{intg.name}</span>
                            <span className="text-xs text-muted-foreground ml-1">- {intg.description}</span>
                            {intg.status === 'coming_soon' && (
                              <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 text-amber-700 dark:text-amber-400 border-amber-500/30">Pronto</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Extra monitors pricing */}
                    <div className="text-xs text-muted-foreground mb-4 p-2 rounded-lg bg-muted/30">
                      <UserPlus className="w-3 h-3 inline mr-1" />
                      Monitor adicional: {fmtPrice(plan.extra_monitor_price_local || plan.extra_monitor_price, plan)} (pago único)
                    </div>

                    <Button
                      data-testid={`select-plan-${plan.id}`}
                      className={cn("w-full gap-2",
                        isCurrent ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30" :
                        plan.popular ? "bg-primary hover:opacity-90" : ""
                      )}
                      variant={isCurrent ? "outline" : plan.popular ? "default" : "outline"}
                      onClick={() => !isCurrent && handleSelectPlan(plan.id)}
                      disabled={isCurrent}
                    >
                      {isCurrent ? 'Plan Actual' : 'Seleccionar'} {!isCurrent && <ArrowRight className="w-4 h-4" />}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 text-center space-y-1">
          <p className="text-sm text-muted-foreground">
            Todos los planes incluyen 14 días de prueba gratuita. Sin compromiso.
          </p>
          <p className="text-xs text-muted-foreground">
            Precios en {regions[selectedRegion]?.currency || 'USD'}. Los impuestos pueden aplicar según tu ubicación.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
